import React, { useState } from 'react'
import propTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import moment from 'moment'

import {
	Button,
	ButtonGroup,
	Chip,
	Grid,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Paper,
	Tooltip,
	Typography,
	makeStyles
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import AddIcon from '@material-ui/icons/AddTwoTone'
import ListIcon from '@material-ui/icons/List'
import GridIcon from '@material-ui/icons/Apps'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import DeleteIcon from '@material-ui/icons/DeleteForever'
import EditIcon from '@material-ui/icons/EditOutlined'

import { Content, Header, HeaderBreadCrum, Layout } from '../../layouts/AuthenticatedLayout'
import SideBar from '../../common/DashboadSideBar'
import DebouncedSearchBox from '../../common/DebouncedSearchBox'
import Toaster from '../../common/Toaster'
import RenderContentIndex from '../../common/renderContentIndex/RenderContentIndex'
import Pagination from '../../common/Pagination/Pagination'
import DirectoryEmptyState from './DirectoryEmptyState'
import ContentAssetCard from './ContentAssetCard'
import CategorySidebar from './CategorySidebar'
import FilterSelect from '../../common/Filters/FilterSelect'
import ContentAssetViewerModal from './ContentAssetViewerModal'
import DeleteResourceConfirmationDialog from './DeleteResourceConfirmationDialog'
import AddResourceWizard from '../wizard/AddResourceWizard'
import { hasAuthorization } from '../../../store/session/sessionActions'
import { PORTAL_ADMINISTRATOR } from '../../../resources/RolesEnum'
// Reuse the dashboard's button-group styles so the Directory's "+ Add" matches
// the create-service "+" button visually by construction.
import { useServiceTableStyles } from '../../../resources/styles/ServicesListStyles'
import { headersTableConfig } from '../resources/ContentAssetTableConfig'
import { getAssetSubtitle, getTypeIcon } from '../resources/ContentAssetTypeIcons'
import { loadContentAssets, deleteContentAsset } from '../../../store/contentDirectory/contentDirectoryActions'
import { openAssetViewer, closeViewer } from '../../../store/contentAssetViewer/ViewerActions'
import { pushDirectoryUrl, replaceDirectoryUrl } from '../UrlState'

const useStyles = makeStyles((theme) => ({
	// The slice-7 PM ask: make the whole view fit the viewport. We pick a
	// header-area offset (~140px = AuthenticatedLayout header + container
	// padding + breadcrumbs) and let the panels claim the rest of the
	// viewport. The toolbar stays put; only the result area (table / cards)
	// scrolls internally.
	root: {
		padding: theme.spacing(1, 2)
	},
	panel: {
		padding: theme.spacing(1, 2),
		height: 'calc(100vh - 140px)',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden'
	},
	sidebarPaper: {
		padding: theme.spacing(1, 1),
		height: 'calc(100vh - 140px)',
		overflowY: 'auto'
	},
	resultArea: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto'
	},
	// Slice 7 polish — override the MUI Grid item md={3}/md={9} split with
	// an explicit 20%/80% ratio (80% of the slice-5 sidebar width). The
	// Grid keeps its xs={12} stacking behavior on narrow viewports; the
	// override only kicks in at the `md` breakpoint and above.
	sidebarColumn: {
		[theme.breakpoints.up('md')]: {
			flexBasis: '20%',
			maxWidth: '20%'
		}
	},
	mainColumn: {
		[theme.breakpoints.up('md')]: {
			flexBasis: '80%',
			maxWidth: '80%'
		}
	},
	headerRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: theme.spacing(2),
		flexWrap: 'wrap',
		marginBottom: theme.spacing(1)
	},
	titles: {
		display: 'flex',
		flexDirection: 'column'
	},
	subtitle: {
		color: theme.palette.text.secondary
	},
	actions: {
		display: 'flex',
		alignItems: 'center'
	},
	tagFilterSlot: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		display: 'inline-flex',
		alignItems: 'center'
	},
	clearFilterSpacer: {
		// The <span> wrapper around the Clear button is required because MUI
		// tooltips don't fire on disabled <Button>s. The span also creates
		// the visible gap between Clear and List view (the inner Button's
		// `marginLeft: -8px` from `buttonsGroup` pulls Clear left within the
		// span, leaving an 8px gap on the right). We want the SAME gap on
		// the Add-side of Clear too — so the span needs `marginLeft: 16px`:
		// 8px to offset the inner Button's `-8px` pull, plus 8px more to be
		// the visible gap.
		marginLeft: theme.spacing(2)
	},
	clearFilterButton: {
		backgroundColor: theme.palette.colors.red,
		color: theme.palette.common.white,
		borderColor: theme.palette.colors.red,
		'&:hover': {
			backgroundColor: theme.palette.colors.red,
			borderColor: theme.palette.colors.red,
			opacity: 0.9
		}
	},
	searchRoot: {
		minWidth: 280,
		marginLeft: theme.spacing(2)
	},
	emptyStateWrapper: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center'
	},
	errorPanel: {
		marginBottom: theme.spacing(2)
	},
	titleCell: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	titleIcon: {
		color: theme.palette.colors && theme.palette.colors.purple ? theme.palette.colors.purple : theme.palette.primary.main
	},
	titleTextStack: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0
	},
	titleText: {
		fontWeight: 600,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	titleSubtitle: {
		color: theme.palette.text.secondary
	},
	tagsCell: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5)
	},
	tagChip: {
		height: 22
	},
	muted: {
		color: theme.palette.text.secondary
	},
	paginationRow: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: theme.spacing(2)
	}
}))

const ADMIN_EMPTY_MESSAGE = 'No records to display, please add one to show information.'
const NON_ADMIN_EMPTY_MESSAGE = 'No resources are available yet.'
const ERROR_MESSAGE = "Couldn't load resources."
const ALL_RESOURCES_LABEL = 'All resources'
const MAX_VISIBLE_TAGS = 3

const AUDIENCE_LABELS = {
	ALL: 'All',
	SERVICE_PROVIDER: 'Service provider',
	CUSTOMER: 'Customer',
	SALES_REPRESENTATIVE: 'Sales rep',
	IMBERA: 'Imbera',
	RETAILER: 'Retailer'
}

const formatAudienceLabel = (code) => AUDIENCE_LABELS[code] || code

const DirectoryDesktopView = (props) => {
	const classes = useStyles()
	const dashboardClasses = useServiceTableStyles()
	const dispatch = useDispatch()
	const history = useHistory()
	const { userProfile, onChangeLocation } = props

	const isPortalAdministrator = hasAuthorization(userProfile, [PORTAL_ADMINISTRATOR])
	const isImberaUser = !!(userProfile && userProfile.Account && userProfile.Account.AccountType === 'IMBERA')
	const canSeeAudience = isPortalAdministrator || isImberaUser

	// Reducer state — single source of truth for everything URL-derived.
	const isLoading = useSelector((state) => state.contentDirectory.get('isLoading'))
	const total = useSelector((state) => state.contentDirectory.get('total'))
	const pageNumber = useSelector((state) => state.contentDirectory.get('pageNumber'))
	const pageSize = useSelector((state) => state.contentDirectory.get('pageSize'))
	const query = useSelector((state) => state.contentDirectory.get('query'))
	const error = useSelector((state) => state.contentDirectory.get('error'))
	const gridMode = useSelector((state) => state.contentDirectory.get('gridMode'))
	const selectedCategoryId = useSelector((state) => state.contentDirectory.get('selectedCategoryId'))
	const allCount = useSelector((state) => state.contentDirectory.get('allCount'))
	const contentAssetsImmutable = useSelector((state) => state.contentDirectory.get('contentAssets'))
	const contentAssets = contentAssetsImmutable && contentAssetsImmutable.toJS ? contentAssetsImmutable.toJS() : []
	const categoryCountsImmutable = useSelector((state) => state.contentDirectory.get('categoryCounts'))
	const categoryCounts = categoryCountsImmutable && categoryCountsImmutable.toJS ? categoryCountsImmutable.toJS() : []
	const selectedTagIdsImmutable = useSelector((state) => state.contentDirectory.get('selectedTagIds'))
	const selectedTagIds = selectedTagIdsImmutable && selectedTagIdsImmutable.toJS ? selectedTagIdsImmutable.toJS() : []

	// Tag list (separate slice from slice 2's wizard cache).
	const tagsImmutable = useSelector((state) => state.contentAssetTags && state.contentAssetTags.get('tags'))
	const tags = tagsImmutable && tagsImmutable.toJS ? tagsImmutable.toJS() : []

	const [isWizardOpen, setIsWizardOpen] = useState(false)
	const [toaster, setToaster] = useState({ open: false, variant: null, message: null })

	// Slice 7 — admin actions. The kebab Menu anchors via viewport coordinates
	// (not the IconButton DOM ref) because the slice-4 DataGrid keys each row
	// with a fresh uuidV4() on every render. Once the kebab click triggers a
	// state update, the DataGrid re-mounts rows with new DOM nodes, orphaning
	// a stored `anchorEl`. anchorPosition by-coordinate sidesteps that — the
	// Menu pops up where the click landed regardless of subsequent re-renders.
	const [kebabMenuPos, setKebabMenuPos] = useState(null) // { top, left } | null
	const [kebabAsset, setKebabAsset] = useState(null)
	const [editingAsset, setEditingAsset] = useState(null)
	const [deletingAsset, setDeletingAsset] = useState(null)
	const [isDeleting, setIsDeleting] = useState(false)
	const [deleteError, setDeleteError] = useState(null)

	// Viewer's currently open asset — used by the delete handler to know
	// whether to close the viewer modal after a successful delete.
	const viewerAssetImmutable = useSelector((state) => state.contentAssetViewer.get('asset'))
	const viewerAsset = viewerAssetImmutable && viewerAssetImmutable.toJS ? viewerAssetImmutable.toJS() : null

	const onAddClick = () => {
		setEditingAsset(null) // ensure wizard opens in create mode
		setIsWizardOpen(true)
	}

	const handlePublishSuccess = (info) => {
		const wasEdit = info && info.wasEdit
		setIsWizardOpen(false)
		setEditingAsset(null)
		setToaster({
			open: true,
			variant: 'success',
			message: wasEdit ? 'Resource updated' : 'Resource published'
		})
		// URL didn't change but data did — dispatch directly to refetch with the
		// current URL state. Index.js's URL-watching useEffect won't re-fire so
		// we use this one-off direct dispatch.
		dispatch(loadContentAssets(query || '', wasEdit ? (pageNumber || 1) : 1, pageSize, selectedCategoryId, selectedTagIds))
	}

	// ── Kebab + admin actions ──────────────────────────────────────────────

	const openKebab = (event, asset) => {
		event.stopPropagation() // don't bubble into the row/card click
		// Capture viewport coordinates of the IconButton (bottom-right
		// corner) BEFORE any re-render can detach the DOM node. Storing
		// numbers instead of a DOM ref makes the Menu immune to the
		// DataGrid's row remounts (see comment on `kebabMenuPos`).
		const rect = event.currentTarget.getBoundingClientRect()
		setKebabMenuPos({ top: rect.bottom, left: rect.right })
		setKebabAsset(asset)
	}
	const closeKebab = () => {
		setKebabMenuPos(null)
		setKebabAsset(null)
	}
	const onEditClick = () => {
		const target = kebabAsset
		closeKebab()
		if (!target) return
		setEditingAsset(target)
		setIsWizardOpen(true)
	}
	const onDeleteClick = () => {
		const target = kebabAsset
		closeKebab()
		if (!target) return
		setDeletingAsset(target)
		setDeleteError(null)
	}

	const onDeleteCancel = () => {
		if (isDeleting) return
		setDeletingAsset(null)
		setDeleteError(null)
	}
	const onDeleteConfirm = async () => {
		if (!deletingAsset) return
		setIsDeleting(true)
		setDeleteError(null)
		try {
			await dispatch(deleteContentAsset(deletingAsset.Id))
			// If the viewer was open for the asset we just deleted, close it —
			// the stream URL would 404 on the next interaction anyway.
			if (viewerAsset && viewerAsset.Id === deletingAsset.Id) {
				dispatch(closeViewer())
			}
			setDeletingAsset(null)
			setToaster({ open: true, variant: 'success', message: 'Resource deleted' })
			// Refresh with current URL state.
			dispatch(loadContentAssets(query || '', pageNumber || 1, pageSize, selectedCategoryId, selectedTagIds))
		} catch (err) {
			const status = err && err.status
			if (status === 403) setDeleteError("You don't have permission to do that.")
			else if (status === 404) setDeleteError('That resource no longer exists.')
			else setDeleteError("Couldn't delete the resource.")
		} finally {
			setIsDeleting(false)
		}
	}

	const handleWizardError = ({ message }) => {
		setToaster({ open: true, variant: 'error', message })
	}

	// URL writers — Index.js's useEffect watches `location.search` and is the
	// only place that calls `loadContentAssets` in the happy path.
	const onSearch = (next) => {
		replaceDirectoryUrl(history, { query: next, pageNumber: 1 })
	}
	const onChangePage = (_event, newPage) => {
		pushDirectoryUrl(history, { pageNumber: newPage })
	}
	const switchViewMode = () => {
		pushDirectoryUrl(history, { gridMode: !gridMode })
	}
	const onSelectCategory = (categoryId) => {
		pushDirectoryUrl(history, { categoryId: categoryId == null ? null : categoryId, pageNumber: 1 })
	}
	// onToggleTag removed — FilterSelect (from the services dashboard) now
	// manages its own URL writes for the `tags` param. Index.js's
	// useEffect([location.search]) still re-dispatches loadContentAssets
	// with the new tag selection picked up by UrlState.parseDirectoryUrl,
	// which accepts both CSV and multi-param formats.
	const onRetry = () => {
		// URL doesn't change — direct dispatch to re-attempt the failed fetch.
		dispatch(loadContentAssets(query || '', pageNumber || 1, pageSize, selectedCategoryId, selectedTagIds))
	}
	const handleAssetClick = (item) => dispatch(openAssetViewer(item))
	// Slice-4's DataGrid passes (sender, item) to onItemClick; we ignore the sender.
	const handleRowClick = (_sender, item) => handleAssetClick(item)
	const onClearFilters = () => {
		// Reset every filter axis to its default in one URL push. View mode is
		// a viewing preference, not a filter, so we leave gridMode untouched.
		pushDirectoryUrl(history, {
			categoryId: null,
			tagIds: [],
			query: '',
			pageNumber: 1
		})
	}

	const hasActiveFilters =
		selectedCategoryId != null ||
		(Array.isArray(selectedTagIds) && selectedTagIds.length > 0) ||
		(typeof query === 'string' && query.length > 0)

	const emptyMessage = isPortalAdministrator ? ADMIN_EMPTY_MESSAGE : NON_ADMIN_EMPTY_MESSAGE
	const totalPages = pageSize > 0 ? Math.ceil((total || 0) / pageSize) : 0
	const hasItems = contentAssets.length > 0
	const showError = !!error
	const showEmpty = !showError && !isLoading && !hasItems
	const showTable = !showError && (hasItems || isLoading)

	// Header subtitle reflects active category (Decision 8 in design.md).
	const activeCategoryName = selectedCategoryId != null
		? (categoryCounts.find((c) => c.CategoryId === selectedCategoryId) || {}).Name
		: null
	const subtitleLabel = activeCategoryName || ALL_RESOURCES_LABEL

	const renderTitleCell = (item) => {
		const TypeIcon = getTypeIcon(item.Type)
		return (
			<div className={classes.titleCell}>
				<TypeIcon className={classes.titleIcon} />
				<div className={classes.titleTextStack}>
					<Typography variant='body2' className={classes.titleText} title={item.Title}>
						{item.Title}
					</Typography>
					<Typography variant='caption' className={classes.titleSubtitle}>
						{getAssetSubtitle(item)}
					</Typography>
				</div>
			</div>
		)
	}

	const renderTagsCell = (item) => {
		const itemTags = Array.isArray(item.Tags) ? item.Tags : []
		if (itemTags.length === 0) {
			return (
				<Typography variant='caption' className={classes.muted}>
					No tags
				</Typography>
			)
		}
		const visible = itemTags.slice(0, MAX_VISIBLE_TAGS)
		const overflow = itemTags.length - visible.length
		return (
			<div className={classes.tagsCell}>
				{visible.map((tag) => (
					<Chip key={tag.Id} size='small' label={tag.Name} className={classes.tagChip} />
				))}
				{overflow > 0 && <Chip size='small' label={`+${overflow}`} className={classes.tagChip} />}
			</div>
		)
	}

	const getHeaderConfiguration = () => {
		const titleColumn = headersTableConfig.find((c) => c.dataSource === 'Title')
		titleColumn.onRenderProperty = renderTitleCell

		const categoryColumn = headersTableConfig.find((c) => c.dataSource === 'CategoryName')
		categoryColumn.onRenderProperty = (item) => (
			<Typography variant='body2'>{item.CategoryName || ''}</Typography>
		)

		const tagsColumn = headersTableConfig.find((c) => c.dataSource === 'Tags')
		tagsColumn.onRenderProperty = renderTagsCell

		const audienceColumn = headersTableConfig.find((c) => c.dataSource === 'Audience')
		audienceColumn.onRenderProperty = (item) => {
			const codes = Array.isArray(item.AudienceCodes) ? item.AudienceCodes : []
			if (codes.length === 0) {
				return (
					<Typography variant='caption' className={classes.muted}>
						—
					</Typography>
				)
			}
			return (
				<div className={classes.tagsCell}>
					{codes.map((code) => (
						<Chip key={code} size='small' label={formatAudienceLabel(code)} className={classes.tagChip} />
					))}
				</div>
			)
		}

		const createdColumn = headersTableConfig.find((c) => c.dataSource === 'CreationDate')
		createdColumn.onRenderProperty = (item) => (
			<Typography variant='caption' className={classes.muted}>
				{item.CreationDate ? moment(item.CreationDate).fromNow() : ''}
			</Typography>
		)

		const actionsColumn = headersTableConfig.find((c) => c.dataSource === 'Actions')
		actionsColumn.onRenderProperty = (item) => {
			// Slice 7: admin-only kebab. Non-admins render nothing — the
			// column header still occupies layout width, but cells stay blank.
			if (!isPortalAdministrator) return null
			return (
				<span onClick={(e) => e.stopPropagation()}>
					<Tooltip title='Actions'>
						<IconButton
							size='small'
							aria-label='actions'
							onClick={(e) => openKebab(e, item)}
						>
							<MoreVertIcon fontSize='small' />
						</IconButton>
					</Tooltip>
				</span>
			)
		}

		// AUDIENCE column is restricted to portal admins and Imbera-account
		// users (client review). Returning a filtered copy keeps the shared
		// `headersTableConfig` array intact for other paths/callers.
		return canSeeAudience
			? headersTableConfig
			: headersTableConfig.filter((c) => c.dataSource !== 'Audience')
	}

	const renderCard = (item) => (
		<ContentAssetCard
			item={item}
			onClick={() => handleAssetClick(item)}
			onKebabClick={isPortalAdministrator ? openKebab : undefined}
			showAudience={canSeeAudience}
		/>
	)

	return (
		<Layout>
			<Header
				key={'AuthenticatedHeader'}
				user={props.user}
				breadcrumbs={
					<HeaderBreadCrum>
						<Typography color='secondary'>Documents</Typography>
					</HeaderBreadCrum>
				}
			/>
			<SideBar key={'AuthenticatedSideBar'} onChangeLocation={onChangeLocation} selectedButtonIndex={14} userProfile={userProfile} />
			<Content key={'AuthenticatedContent'}>
				<div className={classes.root}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={3} className={classes.sidebarColumn}>
							<Paper className={classes.sidebarPaper}>
								<CategorySidebar
									categoryCounts={categoryCounts}
									allCount={allCount}
									selectedCategoryId={selectedCategoryId}
									onSelectCategory={onSelectCategory}
									isLoading={isLoading}
								/>
							</Paper>
						</Grid>
						<Grid item xs={12} md={9} className={classes.mainColumn}>
							<Paper className={classes.panel}>
								<div className={classes.headerRow}>
									<div className={classes.titles}>
										<Typography variant='h5'>Documents</Typography>
										<Typography variant='body2' className={classes.subtitle}>
											{subtitleLabel}
										</Typography>
									</div>
									<div className={classes.actions}>
										<span className={classes.tagFilterSlot}>
											<FilterSelect
												nameFilter='Tags'
												data={tags.map((t) => ({ key: String(t.Id), label: t.Name }))}
												nameFilterUrl='tags'
												isVisibleSearch={tags.length > 6}
												userProfile={userProfile}
												roles={[]}
											/>
										</span>
										<ButtonGroup
											component='span'
											size='small'
											variant='outlined'
											color='primary'
											className={dashboardClasses.buttonsGroupContainer}
										>
											{isPortalAdministrator && (
												<Tooltip component='span' title='Add resource'>
													<Button className={dashboardClasses.buttonsGroup} onClick={onAddClick}>
														<AddIcon />
													</Button>
												</Tooltip>
											)}
											<Tooltip component='span' title={hasActiveFilters ? 'Clear filters' : 'No active filters'}>
												<span className={classes.clearFilterSpacer} style={{ paddingLeft: 16 }}>
													<Button
														className={`${dashboardClasses.buttonsGroup}${hasActiveFilters ? ' ' + classes.clearFilterButton : ''}`}
														disabled={!hasActiveFilters}
														onClick={onClearFilters}
														aria-label='clear filters'
													>
														<DeleteIcon />
													</Button>
												</span>
											</Tooltip>
											<Tooltip component='span' title='List view'>
												<Button
													className={dashboardClasses.buttonsGroup}
													disabled={!gridMode}
													onClick={switchViewMode}
													aria-label='list view'
												>
													<ListIcon />
												</Button>
											</Tooltip>
											<Tooltip component='span' title='Card view'>
												<Button
													className={dashboardClasses.buttonsGroup}
													disabled={gridMode}
													onClick={switchViewMode}
													aria-label='card view'
												>
													<GridIcon />
												</Button>
											</Tooltip>
										</ButtonGroup>
										<div className={classes.searchRoot}>
											<DebouncedSearchBox
												placeholder='Search...'
												initialValue={query || ''}
												onDebouncedChange={onSearch}
											/>
										</div>
									</div>
								</div>

								<div className={classes.resultArea}>
									{showError && (
										<Alert
											severity='error'
											className={classes.errorPanel}
											action={
												<Button color='inherit' size='small' onClick={onRetry}>
													Retry
												</Button>
											}
										>
											{ERROR_MESSAGE}
										</Alert>
									)}

									{showEmpty && (
										<div className={classes.emptyStateWrapper}>
											<DirectoryEmptyState message={emptyMessage} searchQuery={query} />
										</div>
									)}

									{showTable && (
										<RenderContentIndex
											headerConfig={getHeaderConfiguration()}
											isLoading={isLoading}
											data={contentAssets}
											gridMode={gridMode}
											searchValue={query || ''}
											titleData={emptyMessage}
											renderCard={renderCard}
											onRowClick={handleRowClick}
										/>
									)}
								</div>
								{showTable && (
									<div className={classes.paginationRow}>
										<Pagination
											count={Math.max(totalPages, 1)}
											page={pageNumber || 1}
											onChangePage={onChangePage}
										/>
									</div>
								)}
							</Paper>
						</Grid>
					</Grid>
				</div>

				{isPortalAdministrator && (
					<AddResourceWizard
						key={editingAsset ? `edit-${editingAsset.Id}` : 'create'}
						open={isWizardOpen}
						onClose={() => { setIsWizardOpen(false); setEditingAsset(null) }}
						onPublishSuccess={handlePublishSuccess}
						onError={handleWizardError}
						editingAsset={editingAsset}
					/>
				)}

				{/* Slice 7: admin kebab Menu and delete confirmation dialog.
				    anchorReference="anchorPosition" + viewport coords keeps
				    the Menu anchored even if the underlying row's DOM node
				    gets recycled by the DataGrid's uuidV4-keyed rows. */}
				<Menu
					anchorReference='anchorPosition'
					anchorPosition={
						kebabMenuPos ? { top: kebabMenuPos.top, left: kebabMenuPos.left } : undefined
					}
					anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
					open={Boolean(kebabMenuPos)}
					onClose={closeKebab}
					keepMounted
				>
					<MenuItem onClick={onEditClick}>
						<ListItemIcon>
							<EditIcon fontSize='small' />
						</ListItemIcon>
						<ListItemText primary='Edit' />
					</MenuItem>
					<MenuItem onClick={onDeleteClick}>
						<ListItemIcon>
							<DeleteIcon fontSize='small' />
						</ListItemIcon>
						<ListItemText primary='Delete' />
					</MenuItem>
				</Menu>

				<DeleteResourceConfirmationDialog
					open={Boolean(deletingAsset)}
					onCancel={onDeleteCancel}
					onConfirm={onDeleteConfirm}
					isDeleting={isDeleting}
					error={deleteError}
				/>

				{toaster.open && (
					<Toaster
						toasterOptions={toaster}
						onClose={() => setToaster({ open: false, variant: null, message: null })}
					/>
				)}

				<ContentAssetViewerModal />
			</Content>
		</Layout>
	)
}

DirectoryDesktopView.propTypes = {
	userProfile: propTypes.object,
	onChangeLocation: propTypes.func.isRequired
}

DirectoryDesktopView.defaultProps = {
	userProfile: null,
	onChangeLocation: () => console.warn('No [onChangeLocation] callback defined')
}

export default DirectoryDesktopView
